import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Printer, Send, Loader2, QrCode, MessageCircle, Sparkles } from 'lucide-react';
import LotusLogo from './LotusLogo';
import { contactInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../lib/api';

const preset = [500, 1000, 1500, 2000];

const Vouchers = () => {
  const { toast } = useToast();
  const voucherRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ snapscan_link: '', snapscan_qr_base64: '' });

  const [form, setForm] = useState({
    from: '',
    buyerEmail: '',
    to: '',
    amount: 1000,
    message: 'Wishing you a moment of pure bliss.',
  });
  const [issued, setIssued] = useState(null);
  const [occasion, setOccasion] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  const suggestMessage = async () => {
    setSuggesting(true);
    try {
      const r = await publicApi.post('/voucher/suggest-message', {
        recipient_name: form.to,
        from_name: form.from,
        occasion,
        tone: 'warm',
      });
      if (r.data?.text) setForm((f) => ({ ...f, message: r.data.text }));
    } catch {
      toast({ title: 'Could not fetch a suggestion', description: 'Please try again in a moment.' });
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    publicApi.get('/settings').then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const issue = async () => {
    if (!form.from || !form.buyerEmail || !form.to || !form.amount) {
      toast({ title: 'Please fill in the details', description: 'Buyer name, buyer email, recipient and amount are required.' });
      return;
    }
    setSaving(true);
    try {
      const res = await publicApi.post('/voucher', {
        buyer_name: form.from,
        buyer_email: form.buyerEmail,
        recipient_name: form.to,
        recipient_email: form.recipientEmail || '',
        amount: Number(form.amount),
        message: form.message,
      });
      const v = {
        ...form,
        amount: Number(form.amount),
        code: res.data.code,
        date: new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' }),
      };
      setIssued(v);
      toast({
        title: res.data.email_sent ? 'Voucher created & Dawn notified \u2728' : 'Voucher created \u2728',
        description: 'Complete payment below then print or email the voucher.',
      });
      setTimeout(() => voucherRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    } catch (err) {
      toast({ title: 'Could not create voucher', description: 'Please try again in a moment.' });
    } finally {
      setSaving(false);
    }
  };

  const printVoucher = () => {
    if (!voucherRef.current) return;
    const printWindow = window.open('', '_blank', 'width=700,height=900');
    printWindow.document.write(`
      <html><head><title>Blissful Mobile Spa - Gift Voucher</title>
      <link href="https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@200;300;400;500&family=Cormorant+Garamond:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <style>
        body{margin:0;font-family:'Josefin Sans',sans-serif;background:#fff;padding:40px;}
        .card{max-width:560px;margin:auto;padding:40px;border-radius:24px;background:linear-gradient(135deg,#f4a48c 0%,#e9a0aa 30%,#d29ac1 60%,#b8a4d8 85%,#a5b0e6 100%);color:#fff;box-shadow:0 20px 60px rgba(0,0,0,0.2);}
        h1{font-family:'Cormorant Garamond',serif;font-weight:400;font-size:36px;margin:8px 0;letter-spacing:.08em;}
        .amt{font-family:'Cormorant Garamond',serif;font-size:64px;font-weight:500;margin:14px 0;}
        .lbl{letter-spacing:.3em;font-size:11px;opacity:.85;text-transform:uppercase;}
        .row{display:flex;justify-content:space-between;margin-top:24px;font-size:14px;letter-spacing:.15em;}
        .msg{font-family:'Cormorant Garamond',serif;font-style:italic;font-size:20px;margin:16px 0 8px;}
        .code{margin-top:22px;padding:12px;border:1px dashed rgba(255,255,255,.5);border-radius:12px;letter-spacing:.4em;font-size:13px;text-align:center;}
        .foot{margin-top:24px;font-size:11px;letter-spacing:.25em;text-align:center;opacity:.85;}
      </style></head><body onload="window.print();setTimeout(()=>window.close(),300)">
        <div class="card">
          <div style="text-align:center;">
            <div class="lbl">BLISSFULL MOBILE SPA</div>
            <h1>Gift Voucher</h1>
            <div class="amt">R${issued.amount.toLocaleString()}</div>
          </div>
          <div class="row"><div><div class="lbl">To</div><div style="font-size:18px;margin-top:4px;">${issued.to}</div></div>
            <div><div class="lbl">From</div><div style="font-size:18px;margin-top:4px;">${issued.from}</div></div></div>
          <p class="msg">\u201c${issued.message}\u201d</p>
          <div class="code">CODE &middot; ${issued.code}</div>
          <div class="foot">Redeem at ${contactInfo.email} &middot; WhatsApp ${contactInfo.whatsapp} &middot; Issued ${issued.date}</div>
        </div>
      </body></html>
    `);
    printWindow.document.close();
  };

  const emailVoucher = () => {
    if (!issued) return;
    const subject = encodeURIComponent(`Gift Voucher Purchase - R${issued.amount} for ${issued.to}`);
    const body = encodeURIComponent(
`Hi Dawn,

I'd like to purchase the following gift voucher:

Amount: R${issued.amount}
To: ${issued.to}
From: ${issued.from}
Message: ${issued.message}
Code: ${issued.code}
Date: ${issued.date}

Please let me know how to complete payment.

Thank you!`
    );
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  return (
    <section id="vouchers" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p
            className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}
          >
            GIFT VOUCHERS
          </p>
          <h2
            className="text-white font-light"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            }}
          >
            Give the gift of bliss
          </h2>
          <p
            className="text-white/90 mt-4 italic font-light text-lg"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            A beautiful printable voucher in any Rand amount &mdash; perfect for birthdays, anniversaries, or just because.
          </p>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8 }}
            className="bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-8 md:p-10 shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-6 text-white">
              <Gift size={22} strokeWidth={1.5} />
              <span className="tracking-[0.25em] text-xs" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                CUSTOMISE YOUR VOUCHER
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>FROM</label>
                <input value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="Your name" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>YOUR EMAIL</label>
                <input type="email" value={form.buyerEmail} onChange={(e) => setForm({ ...form, buyerEmail: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="you@example.com" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TO</label>
                <input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="Recipient name" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>RECIPIENT&apos;S EMAIL (OPTIONAL)</label>
                <input type="email" value={form.recipientEmail || ''} onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="So we can email them the voucher when paid" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-3" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>AMOUNT (RAND)</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {preset.map((v) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, amount: v })}
                      className={`px-4 py-1.5 rounded-full text-xs tracking-[0.2em] border transition-all ${
                        Number(form.amount) === v ? 'bg-white text-rose-500 border-white' : 'bg-white/10 text-white border-white/40 hover:bg-white/25'
                      }`} style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                      R{v}
                    </button>
                  ))}
                </div>
                <input type="number" min={100} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="Custom amount" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-white/80 text-xs tracking-[0.25em]" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>MESSAGE</label>
                  <div className="flex items-center gap-2">
                    <select value={occasion} onChange={(e) => setOccasion(e.target.value)}
                      className="bg-white/15 border border-white/30 text-white text-xs rounded-full px-3 py-1 outline-none"
                      style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                      <option value="" className="text-neutral-800">Occasion (optional)</option>
                      <option value="Birthday" className="text-neutral-800">Birthday</option>
                      <option value="Anniversary" className="text-neutral-800">Anniversary</option>
                      <option value="Mother's Day" className="text-neutral-800">Mother&apos;s Day</option>
                      <option value="Thank you" className="text-neutral-800">Thank you</option>
                      <option value="New baby" className="text-neutral-800">New baby</option>
                      <option value="Just because" className="text-neutral-800">Just because</option>
                    </select>
                    <button type="button" onClick={suggestMessage} disabled={suggesting}
                      className="inline-flex items-center gap-1 text-xs tracking-[0.15em] px-3 py-1 rounded-full border border-white/60 text-white bg-white/10 hover:bg-white hover:text-rose-500 transition-all disabled:opacity-60"
                      style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                      {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      HELP ME WRITE
                    </button>
                  </div>
                </div>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none resize-none"
                  placeholder="A short personal note" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
            </div>

            <button onClick={issue} disabled={saving}
              className="mt-8 inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] font-light bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500 disabled:opacity-60"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
              {saving ? (<>CREATING <Loader2 size={14} className="animate-spin" /></>) : (<>CREATE VOUCHER <Gift size={14} /></>)}
            </button>
          </motion.div>

          {/* Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center"
          >
            <div ref={voucherRef} className="relative bg-gradient-to-br from-rose-300/60 via-pink-300/50 to-indigo-300/60 backdrop-blur-md border border-white/40 rounded-3xl p-8 md:p-10 shadow-2xl text-white">
              <div className="absolute top-4 right-4 opacity-40">
                <LotusLogo className="w-20 h-20" />
              </div>
              <p className="text-xs tracking-[0.3em]" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>BLISSFULL MOBILE SPA</p>
              <h3 className="font-light mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)' }}>
                Gift Voucher
              </h3>
              <p className="text-white font-light mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.8rem, 6vw, 4rem)' }}>
                R{issued ? issued.amount.toLocaleString() : Number(form.amount || 0).toLocaleString()}
              </p>
              <div className="flex justify-between mt-6 gap-6">
                <div>
                  <p className="text-xs tracking-[0.25em] opacity-90" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TO</p>
                  <p className="text-lg mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{form.to || '\u2014'}</p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.25em] opacity-90" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>FROM</p>
                  <p className="text-lg mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{form.from || '\u2014'}</p>
                </div>
              </div>
              <p className="italic mt-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>&ldquo;{form.message}&rdquo;</p>
              {issued && (
                <div className="mt-6 border border-dashed border-white/50 rounded-xl px-4 py-3 text-center tracking-[0.35em] text-sm" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                  {issued.code}
                </div>
              )}
            </div>

            {issued && (
              <>
                <div className="flex flex-wrap gap-3 mt-5">
                  <button onClick={printVoucher}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
                    style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                    <Printer size={14} /> PRINT
                  </button>
                  <button onClick={emailVoucher}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all"
                    style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                    <Send size={14} /> EMAIL DAWN
                  </button>
                </div>

                <div className="mt-6 bg-white/15 backdrop-blur-md rounded-2xl border border-white/25 p-5">
                  <p className="text-white/90 text-xs tracking-[0.3em] mb-3" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                    COMPLETE PAYMENT
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-5">
                    {settings.snapscan_qr_base64 ? (
                      <img src={settings.snapscan_qr_base64} alt="SnapScan QR" className="w-32 h-32 rounded-xl bg-white p-2 object-contain" />
                    ) : (
                      <div className="w-32 h-32 rounded-xl border border-dashed border-white/50 flex items-center justify-center text-white/80">
                        <QrCode size={38} strokeWidth={1.4} />
                      </div>
                    )}
                    <div className="flex-1 text-white/95 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1rem' }}>
                      <p>Scan Dawn&apos;s SnapScan QR to pay R{issued.amount.toLocaleString()} for voucher <span className="tracking-[0.2em]">{issued.code}</span>, or WhatsApp to arrange EFT/card.</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {settings.snapscan_link && (
                          <a href={settings.snapscan_link} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all"
                            style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                            <QrCode size={13} /> OPEN SNAPSCAN
                          </a>
                        )}
                        <a href={`https://wa.me/${contactInfo.whatsappIntl}?text=${encodeURIComponent(`Hi Dawn! I'd like to pay R${issued.amount} for gift voucher ${issued.code}. Please share your preferred payment method.`)}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all"
                          style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                          <MessageCircle size={13} /> ARRANGE ON WHATSAPP
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Vouchers;
